import { Label, Link, Maybe } from "@models/ISchema";
import { isCourseTypeForAssignationOrProgramming } from "./courseUtils";

/**
 * Group label codes the backend assigns when microprogramming is applied or
 * undone. They are mutually exclusive: `sessionsCrud` flips one for the other
 * inside `ModelAutomations::applyMicroprogrammingLabel`.
 *
 * Both live in the `group_configuration` category, whose source is `EDH` — so
 * they also reach the generic "system labels" list in the group header. The
 * header suppresses them there and renders the dedicated pill instead, to show
 * the product copy rather than the raw database name.
 */
export const PROGRAMMING_LABEL = {
  applied: "group_with_programming_structure",
  pending: "pending_programming_structure",
} as const;

export type ProgrammingGroupState = "pending" | "applied";

const PILL_LABEL_KEYS: Record<ProgrammingGroupState, string> = {
  pending: "dimension.group.programming-pill.pending",
  applied: "dimension.group.programming-pill.applied",
};

/** True for the two labels the pill renders on the group's behalf. */
export const isProgrammingLabelCode = (code?: string | null): boolean =>
  code === PROGRAMMING_LABEL.applied || code === PROGRAMMING_LABEL.pending;

/**
 * Resolves which microprogramming state a group is in, or `null` when the group
 * is outside the microprogramming flow entirely.
 *
 * The state is read from the persisted labels rather than from
 * `link.programmingStructure`, because the labels are exactly what the backend
 * flips on apply and undo, so the pill always mirrors stored state.
 *
 * `null` is a real outcome, not a defensive fallback: the backend only assigns
 * `pending_programming_structure` to groups whose course passes
 * `isMicroprogrammableCourse()`. A group with neither label is one where
 * microprogramming does not apply, and showing "Programación pendiente" there
 * would be wrong.
 */
export const resolveProgrammingGroupState = (
  labels: Maybe<Maybe<Label>[]> | undefined,
): ProgrammingGroupState | null => {
  const codes = (labels ?? []).map(label => label?.code);

  if (codes.includes(PROGRAMMING_LABEL.applied)) return "applied";

  if (codes.includes(PROGRAMMING_LABEL.pending)) return "pending";

  return null;
};

/** The microprogramming state of a link: the one its groups carry. */
export const resolveLinkProgrammingState = (
  link: Maybe<Pick<Link, "groups">> | undefined,
): ProgrammingGroupState | null =>
  resolveProgrammingGroupState((link?.groups ?? []).flatMap(group => group?.labels ?? []));

/** i18n key for the header pill matching a resolved state. */
export const getProgrammingPillLabelKey = (state: ProgrammingGroupState): string =>
  PILL_LABEL_KEYS[state];

export const isLinkValidForProgramming = (link: Link) => {
  const isValidCourseType = isCourseTypeForAssignationOrProgramming(link?.course?.courseType);

  return Boolean(isValidCourseType || link?.programmingStructure?.id);
};
